/**
 * 🎨 APP LAYOUT COMPONENT
 * 
 * Extrage logica de layout pentru utilizatorii autentificați din App.tsx
 * pentru a transforma App.tsx într-un orchestrator curat.
 * 
 * Responsabilități:
 * - Grid layout cu sidebar și main content
 * - HybridNavigation positioning
 * - LunarGrid page special handling
 * - Mobile responsive padding și navigation spacing
 * 
 * Folosește doar CVA variants care funcționează (flexLayout, container)
 * NU folosește gridTemplate cu CSS Grid Template Areas
 */

import React from 'react';
import { useLocation, Outlet } from "react-router";
import { cn } from '../../styles/cva-v2';
import HybridNavigation from '../primitives/HybridNavigation';

interface AppLayoutProps {
  /** Logout handler pentru HybridNavigation */
  onLogout: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Layout component pentru utilizatorii autentificați
 * Gestionează structura grid cu sidebar și main content
 */
const AppLayout = ({
  onLogout,
  className
}: AppLayoutProps) => {
  const location = useLocation();
  
  // Check if we're on LunarGrid page - needs special overflow handling
  const isLunarGridPage = location.pathname === '/lunar-grid';

  return (
    <div className={cn(
      // Grid layout pentru desktop cu sidebar și main content
      // Folosim clase Tailwind directe care funcționează
      'lg:grid lg:grid-cols-[auto_1fr]',
      className
    )}>
      
      {/* Navigation Sidebar/BottomBar */}
      <HybridNavigation 
        onLogout={onLogout}
        showLogout={true}
        data-testid="main-navigation"
      />
      
      {/* Main Content Area */}
      <main className={cn(
        // LunarGrid page special handling - full height cu overflow control
        isLunarGridPage ? 'lg:h-screen lg:overflow-hidden lg:relative' : '',
        
        // Mobile navigation spacing - bottom padding pentru BottomTabBar
        'pb-20 lg:pb-0',
        
        // Content padding - doar pentru pagini normale, nu LunarGrid
        isLunarGridPage ? '':'p-4 lg:p-6'
      )}>
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout; 